import { Box, Image, Text } from '@bonanza-social/moonlight';
import BoxGradient from '@bonanza-social/moonlight/lib/common/Box/variants/BoxGradient/BoxGradient';
import React, { memo, useCallback, useMemo } from 'react';
import { InboxMessageResponse, InboxMessageType } from '../state';
import { inboxMessageIconMap } from '../content/inbox.content';
import { StackNavigationProp, useNavigation } from '@bonanza-social/navigation';
import { Pressable } from 'react-native';
import { getFromNowDateText } from '@bonanza-social/toolkit/utils';
import { useTransmitResolver } from '@bonanza-social/transmit-resolver';
import { useDispatch } from '@bonanza-social/toolkit/state';
import { SmarticoNotificationClicked } from '../state/inbox.slice';
import { launchGame } from '../../../games/lib';

function InboxMessageItem({
  id,
  type,
  category,
  isRead,
  title,
  body,
  cta,
  createdAt,
  imageUrl,
}: InboxMessageResponse) {
  const dispatch = useDispatch();

  const { handleTransmit } = useTransmitResolver();

  const imageName = useMemo(
    () => inboxMessageIconMap[type as InboxMessageType],
    [type],
  );

  const isStatusMessage = useMemo(() => type === 'Status', [type]);

  const textColor = useMemo(() => {
    if (isStatusMessage) {
      return 'accentSurfacePrimary';
    }

    return isRead ? 'darkSurfaceMuted' : 'darkSurfacePrimary';
  }, [isRead, isStatusMessage]);

  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();

  const handleCTA = useCallback(() => {
    if (category === 'Marketing' && !cta.isClicked) {
      dispatch(SmarticoNotificationClicked(id));
    }

    navigation.popToTop();

    switch (cta.type) {
      case 'NavigationRewards':
      case 'RedeemLoyaltyReward':
        return navigation.navigate('Rewards');
      case 'NavigationShop':
        return navigation.navigate('Shop');
      case 'NavigationGames':
        return navigation.navigate('MainTabNavigator', { screen: 'Games' });
      case 'NavigationGameLaunch':
        dispatch(launchGame({ id: cta.details }));
        break;
      case 'NavigationPromotions':
        if (cta.details) {
          return navigation.navigate('PromoDetails', {
            id: cta.details,
          });
        } else {
          return navigation.navigate('Promos');
        }
      case 'NavigationQuests':
        if (cta.details) {
          return navigation.navigate('QuestDetails', {
            id: cta.details,
          });
        } else {
          return navigation.navigate('Quests');
        }
      case 'RedeemCommunityEventReward':
        return navigation.navigate('CommunityJackpot');

      case 'NavigationTransmit':
        handleTransmit();
        break;
      default:
        return;
    }
  }, [category, cta, navigation, dispatch, id, handleTransmit]);

  return (
    <Pressable onPress={handleCTA}>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap="space-400"
        paddingHorizontal="space-600"
        paddingVertical="space-400"
        variant={isStatusMessage ? 'gradient' : 'default'}
        gradient={isStatusMessage ? 'accentBackground' : undefined}
      >
        <Image
          width="space-1000"
          height="space-1000"
          {...(imageUrl
            ? { source: imageUrl }
            : {
                name: imageName,
              })}
          alignSelf="flex-start"
          opacity={isRead ? 0.5 : 1}
        />
        <Box display="flex" flex={1}>
          <Text
            variant="body-m-semi"
            marginBottom="space-100"
            color={textColor}
          >
            {title}
          </Text>
          <Text variant="body-s" color={textColor}>
            {body}
            {cta.text ? (
              <>
                {' '}
                <Text variant="link-small">{cta.text}</Text>
              </>
            ) : null}
          </Text>
          {createdAt && (
            <Text
              variant="body-s"
              color="darkSurfaceMuted"
              marginTop="space-100"
            >
              {getFromNowDateText(createdAt)}
            </Text>
          )}
        </Box>
        <BoxGradient
          opacity={isRead ? 0 : 1}
          width={12}
          height={12}
          gradient="attentionBackground"
          borderRadius="radius-full"
        />
      </Box>
    </Pressable>
  );
}

export default memo(InboxMessageItem);
